<script lang="ts">
  // Route bullet for the alerts screen: unified RouteBadge for subway lines, rounded tag for bus routes.
  import { isFerryRoute, isNjBusRoute, isRailRoute, isSubwayRoute } from '../../ui/colors'
  import { useFerryStore } from '../../store/ferry'
  import { useRailStore } from '../../store/rail'
  import { useNjBusStore } from '../../store/njbus'
  import { storeSelect } from '../../lib/store.svelte'
  import { LINE_LABEL } from './lines'
  import BusRouteBadge from '../../components/BusRouteBadge.svelte'
  import RouteBadge from '../../components/RouteBadge.svelte'
  import RailRouteBadge from '../../components/RailRouteBadge.svelte'
  import NjBusRouteBadge from '../../components/NjBusRouteBadge.svelte'

  interface Props {
    routeId: string
    /** Subway/ferry disc size in px; smaller inside running text. */
    size?: number
  }

  const { routeId, size = 20 }: Props = $props()

  const subway = $derived(isSubwayRoute(routeId))
  const ferry = $derived(isFerryRoute(routeId))

  // A ferry route's colour and short name come from NYC Ferry's own feed (ui/colors'
  // registry), which may not have loaded on an alerts-only session. Ask for it, and redraw
  // the bullet once it lands — the colour helpers read a plain table, not reactive state.
  const ferryNetwork = storeSelect(useFerryStore, (s) => s.network)
  $effect(() => {
    if (ferry) useFerryStore.getState().ensureLoaded()
  })

  // The same for a LIRR / Metro-North branch, whose colour and code come from the railroads'
  // own feeds (store/rail).
  const rail = $derived(isRailRoute(routeId))
  const railNetwork = storeSelect(useRailStore, (s) => s.network)
  $effect(() => {
    if (rail) useRailStore.getState().ensureLoaded()
  })

  // And an NJ Transit route, whose id (`NJB-12`) is not the number on the bus (`113`): the
  // number is only known once store/njbus has loaded NJ Transit's network.
  const njBus = $derived(isNjBusRoute(routeId))
  const njBusNetwork = storeSelect(useNjBusStore, (s) => s.network)
  $effect(() => {
    if (njBus) useNjBusStore.getState().ensureLoaded()
  })
</script>

{#if njBus}
  {#key njBusNetwork.value}
    <NjBusRouteBadge {routeId} size="xs" />
  {/key}
{:else if rail}
  {#key railNetwork.value}
    <RailRouteBadge {routeId} {size} />
  {/key}
{:else if ferry}
  {#key ferryNetwork.value}
    <RouteBadge {routeId} {size} />
  {/key}
{:else if subway}
  <RouteBadge {routeId} {size} />
{:else}
  <BusRouteBadge {routeId} label={LINE_LABEL[routeId] ?? routeId} size="xs" />
{/if}
